import { useEffect, useRef } from "react";

// Cycle pool — weighted to favor Pixel and minimize Symbols.
// Pixel appears 5×, Strong/Normal/Mix 2× each, Symbols just 1×.
const FONT_POOL = [
    "vc-font-pixel",
    "vc-font-pixel",
    "vc-font-pixel",
    "vc-font-pixel",
    "vc-font-pixel",
    "vc-font-strong",
    "vc-font-strong",
    "vc-font-normal",
    "vc-font-normal",
    "vc-font-mix",
    "vc-font-mix",
    "vc-font-symbols",
];

/**
 * Renders `text` as inline character spans that cycle through the FT Activica
 * variants in a random order per-letter, then settle on FT Activica Pixel.
 * Fires once per mount when the element first scrolls into view.
 *
 * Default total animation: 1000ms.
 */
export default function ScrambleText({
    text,
    className = "",
    style = {},
    testId,
    duration = 1000,
}) {
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const run = () => {
            const chars = el.querySelectorAll(".vc-char");
            const STEPS = 4; // 4 random fonts before landing on Pixel
            const STEP_DUR = duration / (STEPS + 1);

            chars.forEach((ch) => {
                if (!ch.textContent || ch.textContent.trim() === "") {
                    ch.classList.add("vc-font-pixel");
                    return;
                }
                // Sample STEPS random entries from the weighted pool
                const order = [];
                for (let s = 0; s < STEPS; s += 1) {
                    order.push(
                        FONT_POOL[Math.floor(Math.random() * FONT_POOL.length)],
                    );
                }
                let i = 0;
                const allClasses = [
                    "vc-font-mix",
                    "vc-font-pixel",
                    "vc-font-strong",
                    "vc-font-normal",
                    "vc-font-symbols",
                ];
                const tick = () => {
                    ch.classList.remove(...allClasses);
                    ch.classList.add(order[i]);
                    i += 1;
                    if (i < order.length) {
                        setTimeout(tick, STEP_DUR);
                    } else {
                        setTimeout(() => {
                            ch.classList.remove(...allClasses);
                            ch.classList.add("vc-font-pixel");
                        }, STEP_DUR);
                    }
                };
                tick();
            });
        };

        const obs = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    run();
                    obs.disconnect();
                }
            },
            { threshold: 0.3 },
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [text, duration]);

    return (
        <span
            ref={ref}
            data-testid={testId}
            className={className}
            style={style}
        >
            {Array.from(String(text)).map((ch, i) => (
                <span
                    key={`${i}-${ch}`}
                    className="vc-char vc-font-strong"
                    aria-hidden={ch === " "}
                >
                    {ch}
                </span>
            ))}
        </span>
    );
}
